import React, { useState, useEffect } from 'react';
import { getAvailableMembers, saveInstanceConfig } from '../../services/InstanceService';
import { useInstance } from '../../contexts/InstanceContext';
import './SetupWizard.css';

const SetupWizard = () => {
  const { updateMemberId } = useInstance();
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadMembers();
  }, []);

  const loadMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAvailableMembers();
      if (response.data.success) {
        setMembers(response.data.members);
      } else {
        setError(response.data.message || 'Failed to load members');
      }
    } catch (err) {
      console.error('Failed to load members:', err);
      setError('Failed to load available sites. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async () => {
    if (!selectedMember) return;

    try {
      setSaving(true);
      setError(null);
      const response = await saveInstanceConfig(selectedMember.id);
      if (response.data.success) {
        updateMemberId(selectedMember.id, selectedMember.name || selectedMember.eName);
      } else {
        setError(response.data.message || 'Failed to save configuration');
      }
    } catch (err) {
      console.error('Failed to save configuration:', err);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filteredMembers = members.filter((member) => {
    const search = searchTerm.toLowerCase();
    return (
      member.name?.toLowerCase().includes(search) ||
      member.eName?.toLowerCase().includes(search) ||
      member.email?.toLowerCase().includes(search) ||
      member.brokerageName?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="setup-wizard">
        <div className="setup-wizard__loading">
          <div className="setup-wizard__spinner"></div>
          <p>Loading available sites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-wizard">
      <div className="setup-wizard__container">
        <div className="setup-wizard__header">
          <h1 className="setup-wizard__title">Site Setup</h1>
          <p className="setup-wizard__description">
            Select the site owner for this instance. This setting will be saved permanently on the
            server.
          </p>
        </div>

        {error && (
          <div className="setup-wizard__error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        <div className="setup-wizard__search">
          <input
            type="text"
            placeholder="Search by name, email, or brokerage..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="setup-wizard__member-list">
          {filteredMembers.length === 0 ? (
            <div className="setup-wizard__empty">
              {searchTerm ? 'No members found matching your search.' : 'No members available.'}
            </div>
          ) : (
            filteredMembers.map((member) => (
              <div
                key={member.id}
                className={`setup-wizard__member-card ${
                  selectedMember?.id === member.id ? 'selected' : ''
                }`}
                onClick={() => setSelectedMember(member)}
              >
                <div className="setup-wizard__member-info">
                  <div className="setup-wizard__member-name">
                    {member.name}
                    {member.eName && <span className="setup-wizard__member-ename">({member.eName})</span>}
                  </div>
                  <div className="setup-wizard__member-email">{member.email}</div>
                  {member.brokerageName && (
                    <div className="setup-wizard__member-brokerage">{member.brokerageName}</div>
                  )}
                </div>
                <div className="setup-wizard__member-id">ID: {member.id}</div>
              </div>
            ))
          )}
        </div>

        <div className="setup-wizard__footer">
          <div className="setup-wizard__selected-info">
            {selectedMember ? (
              <span>
                Selected: <strong>{selectedMember.name || selectedMember.eName}</strong>
              </span>
            ) : (
              <span>Select a member to continue</span>
            )}
          </div>
          <button
            className="setup-wizard__button"
            onClick={handleSelect}
            disabled={!selectedMember || saving}
          >
            {saving ? 'Saving...' : 'Confirm Selection'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;
